'use client'

import { BotIcon, ChevronDownIcon } from 'lucide-react'

import { type FC, forwardRef, useState, useEffect } from 'react'
import { AssistantModalPrimitive } from '@assistant-ui/react'

import { Thread } from '~/components/assistant-ui/thread'
import { TooltipIconButton } from '~/components/assistant-ui/tooltip-icon-button'

export function AssistantModal() {
	const [isOpen, setIsOpen] = useState(false)

	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : ''
		return () => {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	return (
		<>
			{isOpen && (
				<div
					style={{
						position: 'fixed',
						inset: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.35)',
						zIndex: 9998,
						transition: 'opacity 0.3s ease',
					}}
					onClick={() => setIsOpen(false)}
				/>
			)}
			<AssistantModalPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
				<AssistantModalPrimitive.Anchor className="aui-root aui-modal-anchor fixed right-4 bottom-4 size-11 z-[9999]">
					<AssistantModalPrimitive.Trigger asChild>
						<AssistantModalButton />
					</AssistantModalPrimitive.Trigger>
				</AssistantModalPrimitive.Anchor>
				<AssistantModalPrimitive.Content
					sideOffset={16}
					className="aui-root aui-modal-content data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-1/2 data-[state=closed]:slide-out-to-right-1/2 data-[state=closed]:zoom-out data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-1/2 data-[state=open]:slide-in-from-right-1/2 data-[state=open]:zoom-in z-[9999] h-[50.25rem] w-[40rem] overflow-clip overscroll-contain rounded-xl border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=closed]:animate-out data-[state=open]:animate-in [&>.aui-thread-root]:bg-inherit [&>.aui-thread-root_.aui-thread-viewport-footer]:bg-inherit"
				>
					<Thread />
				</AssistantModalPrimitive.Content>
			</AssistantModalPrimitive.Root>
		</>
	)
}

type AssistantModalButtonProps = { 'data-state'?: 'open' | 'closed' }

const AssistantModalButton = forwardRef<HTMLButtonElement, AssistantModalButtonProps>(({ 'data-state': state, ...rest }, ref) => {
	const tooltip = state === 'open' ? 'Chiudi Assistente' : 'Apri Assistente'

	return (
		<TooltipIconButton
			variant="default"
			tooltip={tooltip}
			side="left"
			{...rest}
			className="aui-modal-button size-full rounded-full shadow transition-all hover:scale-110 active:scale-90"
			ref={ref}
		>
			<BotIcon
				data-state={state}
				className="aui-modal-button-closed-icon absolute size-6 transition-all data-[state=closed]:rotate-0 data-[state=open]:rotate-90 data-[state=closed]:scale-100 data-[state=open]:scale-0"
			/>

			<ChevronDownIcon
				data-state={state}
				className="aui-modal-button-open-icon absolute size-6 transition-all data-[state=closed]:-rotate-90 data-[state=open]:rotate-0 data-[state=closed]:scale-0 data-[state=open]:scale-100"
			/>
			<span className="aui-sr-only sr-only">{tooltip}</span>
		</TooltipIconButton>
	)
})

AssistantModalButton.displayName = 'AssistantModalButton'
